import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

// Convert 24-hour format to 12-hour format
function formatTime12h(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Format time slot(s) from audit log details - handles both time_slot (string) and time_slots (array)
function formatTimeSlotDisplay(details) {
  if (!details) return '';
  
  // Handle single time_slot
  if (details.time_slot) {
    return formatTime12h(details.time_slot);
  }
  
  // Handle time_slots array
  if (details.time_slots && Array.isArray(details.time_slots) && details.time_slots.length > 0) {
    const sorted = [...details.time_slots].sort();
    const startTime = sorted[0];
    
    // Calculate end time: last slot + 30 minutes
    const lastSlot = sorted[sorted.length - 1];
    const [hours, minutes] = lastSlot.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 30;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    
    return `${formatTime12h(startTime)} - ${formatTime12h(endTime)}`;
  }
  
  return '';
}

function AdminAuditLog({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [timezone, setTimezone] = useState('America/New_York');
  const [actionFilter, setActionFilter] = useState('all');

  // All possible action types
  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'CREATE_BOOKING_FOR_USER', label: 'Create Booking' },
    { value: 'CREATE_RECURRING_BOOKING', label: 'Create Recurring Booking' },
    { value: 'APPROVE_BOOKING', label: 'Approve Booking' },
    { value: 'REJECT_BOOKING', label: 'Reject Booking' },
    { value: 'DELETE_BOOKING', label: 'Delete Booking' },
    { value: 'BULK_APPROVE', label: 'Bulk Approve' },
    { value: 'BULK_REJECT', label: 'Bulk Reject' },
    { value: 'BULK_DELETE', label: 'Bulk Delete' },
    { value: 'SEND_REMINDER', label: 'Send Reminder' },
    { value: 'CREATE_INVOICE', label: 'Create Invoice' },
    { value: 'RECORD_PAYMENT', label: 'Record Payment' },
    { value: 'DELETE_INVOICE', label: 'Delete Invoice' },
    { value: 'create', label: 'Create Blackout' },
    { value: 'update', label: 'Update Blackout' },
    { value: 'delete', label: 'Delete Blackout' },
  ];

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/audit-log?limit=100&offset=0`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', { timeZone: timezone });
  };

  const formatBookingDate = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { timeZone: timezone });
  };

  const getActionBadge = (action) => {
    const colors = {
      CREATE_BOOKING_FOR_USER: '#3b82f6',
      CREATE_RECURRING_BOOKING: '#3b82f6',
      APPROVE_BOOKING: '#22c55e',
      REJECT_BOOKING: '#f59e0b',
      DELETE_BOOKING: '#ef4444',
      BULK_APPROVE: '#22c55e',
      BULK_REJECT: '#f59e0b',
      BULK_DELETE: '#ef4444',
      SEND_REMINDER: '#8b5cf6',
      CREATE_INVOICE: '#10b981',
      RECORD_PAYMENT: '#059669',
      DELETE_INVOICE: '#ef4444',
      delete: '#ef4444',
      create: '#6366f1',
      update: '#6366f1',
      CREATE_BLACKOUT: '#6366f1',
      UPDATE_BLACKOUT: '#6366f1',
      DELETE_BLACKOUT: '#ef4444'
    };
    const labels = {
      CREATE_BOOKING_FOR_USER: 'Created Booking',
      CREATE_RECURRING_BOOKING: 'Created Recurring',
      APPROVE_BOOKING: 'Approved',
      REJECT_BOOKING: 'Rejected',
      DELETE_BOOKING: 'Deleted Booking',
      BULK_APPROVE: 'Bulk Approve',
      BULK_REJECT: 'Bulk Reject',
      BULK_DELETE: 'Bulk Delete',
      SEND_REMINDER: 'Sent Reminder',
      CREATE_INVOICE: 'Created Invoice',
      RECORD_PAYMENT: 'Recorded Payment',
      DELETE_INVOICE: 'Deleted Invoice',
      delete: 'Deleted',
      create: 'Created',
      update: 'Updated',
      CREATE_BLACKOUT: 'Blackout Created',
      UPDATE_BLACKOUT: 'Blackout Updated',
      DELETE_BLACKOUT: 'Blackout Deleted'
    };
    return (
      <span className="action-badge" style={{ background: colors[action] || '#6b7280' }}>
        {labels[action] || action}
      </span>
    );
  };

  const getNotes = (log) => {
    const details = log.details;
    if (!details) return '-';
    
    // For invoice actions, show invoice number
    if (log.entity_type === 'invoice' && details.invoice_number) {
      return `Invoice #${details.invoice_number}`;
    }
    
    // For customer actions, show customer name
    if (log.entity_type === 'user' && details.customer_name) {
      return details.customer_name;
    }
    
    return details.admin_notes || details.reason || '-';
  };

  // Filter logs by action type
  const filteredLogs = actionFilter === 'all' 
    ? logs 
    : logs.filter(log => log.action === actionFilter);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/audit-log/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Export failed');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export audit log');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="admin-audit-log">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Audit Log</h2>
          <p className="log-description" style={{ margin: '4px 0 0 0' }}>Track all administrative actions for compliance and review.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '180px'
            }}
          >
            {actionTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <button 
            onClick={handleExport} 
            disabled={exporting || loading}
            style={{ 
              background: '#059669', 
              color: 'white', 
              border: 'none', 
              padding: '8px 16px', 
              borderRadius: '6px', 
              cursor: exporting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            📥 {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : filteredLogs.length === 0 ? (
        <p className="no-data">{actionFilter === 'all' ? 'No audit log entries' : `No "${actionTypes.find(t => t.value === actionFilter)?.label}" entries found`}</p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Admin</th>
                <th>Action</th>
                <th>Booking Date/Time</th>
                <th>Customer</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr key={log.id}>
                  <td><small>{formatDate(log.created_at)}</small></td>
                  <td>{log.admin_first_name && log.admin_last_name 
                    ? `${log.admin_first_name} ${log.admin_last_name}` 
                    : (log.admin_username || 'System')}</td>
                  <td>{getActionBadge(log.action)}</td>
                  <td>
                    {log.details?.booking_date 
                      ? (<>
                          <div>{formatBookingDate(log.details.booking_date)}</div>
                          {formatTimeSlotDisplay(log.details) && (
                            <small style={{ color: '#6b7280' }}>{formatTimeSlotDisplay(log.details)}</small>
                          )}
                        </>)
                      : '-'}
                  </td>
                  <td>{log.customer_first_name && log.customer_last_name 
                    ? `${log.customer_first_name} ${log.customer_last_name}` 
                    : (log.customer_username || '-')}</td>
                  <td className="details-cell">
                    {getNotes(log)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '12px' }}>
            Showing {filteredLogs.length} of {logs.length} events{actionFilter !== 'all' && ' (filtered)'}. Export CSV for full history.
          </p>
        </>
      )}
    </div>
  );
}

export default AdminAuditLog;
